import type { BulkPlanStatus } from '@simpleacs/shared';

/**
 * Which bulk run the page should be watching, decided from evidence that can
 * disagree with itself.
 *
 * The bulk page learns about a run from two reads that are not taken at the
 * same moment: the history list (`['bulk-plans']`, every 15 s and once right
 * after the 202) and the live plan (`['bulk-plan-live', id]`, every 2 s while
 * a run is attached). On 2026-09-13 the operator started a one-ONU resync and
 * the page went white until a refresh. The nginx log had the whole story in
 * one second: the 202, then 28 alternating `GET /bulk-actions/plans?limit=25`
 * and `GET /notifications`, then silence and a full reload. The run had
 * finished before the history list — fetched while it was still in flight —
 * was replaced, so the live plan said `executed` while the cached list still
 * said `running`. The settle effect cleared `runningId`; the re-attach effect
 * saw a running plan in history and put the same id back; the live query's
 * cached answer for that id was already `executed`, so it settled again, each
 * turn firing two invalidations, until React gave up with "Maximum update
 * depth exceeded" — and with no error boundary anywhere, an unmounted tree.
 *
 * So adoption is decided HERE, from everything the page knows, rather than
 * from the history row alone: a plan this page has already watched settle is
 * never re-adopted whatever the list says, and neither is a plan whose live
 * record — the only read that knows — is already past `running`. Pure, so the
 * exact production shape is pinned in a test that needs no React.
 *
 * "Past `running`" is a narrower phrase than "not `running`", and the first
 * cut of this file got that wrong. A plan has FIVE statuses, and `draft` is
 * the one it wears before `/execute` claims it — which is exactly what the
 * live query's first read returns for a run started from this page: the
 * click sets `runningId` in `onMutate`, the live `GET` leaves in the same
 * commit as the `POST`, and the `GET` (two reads) answers before the `POST`
 * has matched targets, checked limits and prepared the action. Treating that
 * `draft` as "over" marked the page's own run settled before it began: no
 * bar, no Stop, "0 applied." flashed, and when the 202 landed the same id was
 * refused for the page's life — the crash had been traded for a silently
 * orphaned run. Only `executed`, `failed` and `expired` are the run being
 * over; `draft` is the run not having started, and says nothing either way.
 */
export interface RunAdoptionEvidence {
  /** The run the page is watching now; null when it is watching nothing. */
  runningId: string | null;
  /** The history list as cached — may be older than the live plan. */
  historyItems: ReadonlyArray<{ id: string; status: BulkPlanStatus }> | undefined;
  /** Every plan id this page has seen leave `running`, for the page's life. */
  settled: ReadonlySet<string>;
  /**
   * What the live query already holds for a plan, if anything: the cached
   * `['bulk-plan-live', id]` status. Undefined when never read.
   */
  cachedStatus: (id: string) => BulkPlanStatus | undefined;
}

/**
 * Whether a plan status is the run being OVER.
 *
 * The one place the vocabulary is spelled out, so that letting go and taking
 * hold read from the same list. `running` is a run in flight; `draft` is a
 * run that has not started (or was refused before it could — nothing was
 * written, nothing is running, and the same draft may be executed again);
 * the three that remain are terminal. Spelled positively rather than as
 * `!== 'running'` because the negative form is what quietly swept `draft` in.
 */
export function isTerminalRunStatus(status: BulkPlanStatus): boolean {
  return status === 'executed' || status === 'failed' || status === 'expired';
}

/**
 * The id the page should watch after this render, or null for none.
 *
 * Returns `runningId` unchanged while a run is attached — adoption is only
 * for a page watching nothing, and the settle effect is what releases it.
 */
export function nextRunningId(evidence: RunAdoptionEvidence): string | null {
  const { runningId, historyItems, settled, cachedStatus } = evidence;
  if (runningId !== null) return runningId;
  for (const item of historyItems ?? []) {
    if (item.status !== 'running') continue;
    // Watched to the end once already: the list is stale, not the run alive.
    if (settled.has(item.id)) continue;
    // The live record is the read that knows; a cached TERMINAL answer for
    // this id outranks a `running` in an older list. A cached `draft` does
    // not: it is the read this page took before its own `/execute` claimed
    // the plan, and it says the run had not started yet, not that it is over.
    const known = cachedStatus(item.id);
    if (known !== undefined && isTerminalRunStatus(known)) continue;
    return item.id;
  }
  return null;
}

/**
 * Whether the live plan the page is watching is OVER.
 *
 * Kept beside `nextRunningId` so the two halves of the decision — when to let
 * go and when to take hold — read from the same status vocabulary,
 * `isTerminalRunStatus`. `executed`, `failed` and `expired` are the run being
 * over. `running` is not, and neither is `draft`: that is the answer the live
 * query gets for the page's own run before the `POST` has claimed the plan,
 * and settling on it disowned every run started from this page — the bar and
 * Stop never appeared, "0 applied." flashed, and the settled set then barred
 * the id for good. A refused execute leaves the same `draft` behind, and the
 * operator runs that draft again once the confirmation is right.
 */
export function runHasSettled(
  runningId: string | null,
  live: { status: BulkPlanStatus } | undefined,
): boolean {
  return runningId !== null && live !== undefined && isTerminalRunStatus(live.status);
}

/**
 * Record a plan as settled; true only the FIRST time for that id.
 *
 * The settle effect runs on every change of the live data, and a cached
 * `executed` plan can re-enter it more than once in one commit. The first
 * pass owns the notice and the invalidations; later passes for the same id
 * do nothing — which is what stops the second half of the loop above even
 * if adoption were ever to slip.
 */
export function markSettled(settled: Set<string>, id: string): boolean {
  if (settled.has(id)) return false;
  settled.add(id);
  return true;
}
